import {
  Button,
  CategoriesFormSocialNetworkComponent,
  Category,
  DakenService,
  LogService,
  ObjectHelper,
  SocialNetworksAvailables,
  SubCategoriesFormSocialNetworkComponent,
  ToastHelper,
} from '@hub/shared'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SqModal, SqSteps } from '@squidit/react-css'
import './modal-edit-categories-social-network.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean
  selectedCategories?: Category[]
  onOpenChange?: (open: boolean) => any
  onCategoriesSave?: (categories: Category[]) => any
  socialNetwork: SocialNetworksAvailables
  idSocial: string
}

export default ({ open, onOpenChange, onCategoriesSave, className = '', selectedCategories = [], socialNetwork, idSocial }: Props) => {
  const [t] = useTranslation('modalEditCategoriesSocialNetwork')
  const [height, setHeight] = useState(window.innerHeight)
  const [width, setWidth] = useState(window.innerWidth)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const [step, setStep] = useState(0)
  const [validCategories, setValidCategories] = useState(false)
  const [categories, setCategories] = useState<Category[]>(selectedCategories)
  const [subCategories, setSubCategories] = useState<Category[] | any>([])
  const dakenService = useMemo(() => new DakenService(), [])
  const logService = useMemo(() => new LogService(), [])
  const toast = new ToastHelper().toast
  logService.init('Modal Edit Categories Network Component')

  const callOnOpenChange = useCallback(
    (open: boolean) => {
      if (onOpenChange && typeof onOpenChange === 'function') {
        onOpenChange(open)
      }
    },
    [onOpenChange]
  )

  const editCategories = useCallback(async () => {
    if (!loadingSubmit) {
      setLoadingSubmit(true)
      if (step === 0) {
        setStep(1)
      }
      if (step === 1) {
        try {
          const payload: {
            categories: number[]
          } = {
            categories: [...categories?.map((c) => c.id), ...subCategories?.map((c: Category) => c.id)],
          }
          await dakenService.putProfileCategories(idSocial, payload)
          toast.success(`<strong>${t('success')}</strong>`, {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
        } catch (error) {
          toast.error(`<strong>${t('error')}</strong>`, {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 3600000,
            notOverClick: true,
            immediately: true,
          })
          logService.error({
            error,
            message: 'Error on edit categories',
          })
        }
        if (onCategoriesSave && typeof onCategoriesSave === 'function') {
          onCategoriesSave(
            categories?.map((c) => {
              return {
                ...c,
                children: subCategories?.filter((sb: Category) => {
                  return sb?.parentId === c?.id
                }),
              }
            })
          )
        }
      }
      setLoadingSubmit(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSubmit, categories, subCategories, step, t, onCategoriesSave, socialNetwork, idSocial])

  const backButton = useCallback(() => {
    if (!loadingSubmit) {
      if (step === 0) {
        callOnOpenChange(false)
      }

      if (step === 1) {
        setStep(0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSubmit, step])

  useEffect(() => {
    if (!objectHelper?.compareObjects(categories, selectedCategories)) {
      setCategories(selectedCategories)
      setSubCategories(selectedCategories?.map((c) => c?.children)?.flat() || [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories])

  useEffect(() => {
    setStep(0)
  }, [open])

  useEffect(() => {
    const onResize = () => {
      setHeight(window.innerHeight)
      setWidth(window.innerWidth)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <SqModal
      forceMobileNoMargin
      modalSize="lg"
      className={`modal-edit-categories ${className}`}
      header={<h5 className="modal-edit-categories-title mb-0">{t('title')}</h5>}
      footer={
        <div className="modal-edit-categories-footer">
          <Button
            size="sm"
            disabled={loadingSubmit}
            onClick={() => backButton()}
            inverted
            color="transparent"
            borderColor="transparent"
            textColor="var(--primary_color)"
            id="cancel-button-modal-categories"
            className="cancel-button-modal-categories"
          >
            {step === 0 ? t('cancel') : t('back')}
          </Button>
          <Button
            size="sm"
            loading={loadingSubmit}
            disabled={!validCategories}
            onClick={() => editCategories()}
            id="next-button-modal-categories"
          >
            {step === 0 ? t('next') : t('save')}
          </Button>
        </div>
      }
      open={open}
      onOpenChange={(open: boolean) => callOnOpenChange(open)}
      onCloseChange={() => callOnOpenChange(false)}
    >
      <SqSteps className="steps-edit-categories" steps={[{ status: 'one' }, { status: 'two' }]} active={step} />
      <div
        className="wrapper-categories scrollbar"
        style={{
          maxHeight: `calc(${height}px - 65px - 2rem - 40px - 32px)`,
          minHeight: width < 991 ? `calc(${height}px - 65px - 2rem - 40px - 32px)` : '',
        }}
      >
        {step === 0 ? (
          <CategoriesFormSocialNetworkComponent
            externalCategories={categories}
            onCategoriesValidate={(valid: boolean) => setValidCategories(valid)}
            onCategoriesChange={(categories: Category[]) => setCategories(categories)}
          />
        ) : null}
        {step === 1 ? (
          <SubCategoriesFormSocialNetworkComponent
            parentCategories={categories}
            externalCategories={subCategories}
            onCategoriesChange={(categories: Category[]) => setSubCategories(categories)}
          />
        ) : null}
      </div>
    </SqModal>
  )
}
